import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, PoundSterling, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PlatformFeeModePanel — Phase R1.2 (Feb 7, 2026).
 *
 * Lets the instructor pick who covers the platform fee. The whole panel
 * is hidden when the platform-fee feature flag is off (so production
 * never shows a setting that has no effect).
 *
 * Backed by GET/PUT /api/lesson-payments/fee-mode. The "example at £45"
 * row is rendered straight from the backend's _compute_pricing snapshot,
 * so we never duplicate the math on the client.
 */
export default function PlatformFeeModePanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/lesson-payments/fee-mode`, { withCredentials: true });
      setData(data);
    } catch {
      // Quietly hide on error — non-critical.
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const choose = async (mode) => {
    if (saving || !data || mode === data.platform_fee_mode) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/lesson-payments/fee-mode`,
        { platform_fee_mode: mode },
        { withCredentials: true },
      );
      toast.success(mode === "pupil_pays" ? "Pupils now cover the platform transaction fee" : "You now cover the platform fee");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data || !data.platform_fee_enabled) return null;

  const ex = data.example_at_4500p;
  const ratePct = (data.platform_fee_bps / 100).toFixed(2).replace(/\.?0+$/, "");

  return (
    <section data-testid="fee-mode-panel" className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
      <div className="flex items-center gap-2 mb-1">
        <PoundSterling className="w-4 h-4 text-emerald-600" />
        <h3 data-testid="fee-mode-title" className="text-sm font-extrabold text-slate-900 dark:text-white">
          Who covers the platform fee?
        </h3>
      </div>
      <p className="text-[11px] text-slate-500 mb-3">
        Platform fee is {ratePct}% of the lesson price. Choose whether it comes out of your earnings, or is added on top as a platform transaction fee for the pupil.
      </p>

      <div className="space-y-2">
        <ModeOption
          testid="fee-mode-instructor"
          active={data.platform_fee_mode === "instructor_pays"}
          saving={saving}
          onClick={() => choose("instructor_pays")}
          title="I cover it"
          subtitle={`Pupil pays £45.00 · you receive £41.85`}
        />
        <ModeOption
          testid="fee-mode-pupil"
          active={data.platform_fee_mode === "pupil_pays"}
          saving={saving}
          onClick={() => choose("pupil_pays")}
          title="Add as platform transaction fee to pupil"
          subtitle={`Pupil pays £48.15 · you receive £45.00`}
        />
      </div>

      {/* Live example using the BACKEND's snapshot — not recomputed here. */}
      <div data-testid="fee-mode-example" className="mt-3 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-dashed border-slate-200 dark:border-slate-700">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
          Worked example · 1-hour lesson at £45
        </p>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-slate-500">Lesson price</span>
          <span className="font-bold text-slate-700 dark:text-slate-300">£{(ex.base_amount_pence / 100).toFixed(2)}</span>
        </div>
        {ex.booking_fee_pence > 0 && (
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-slate-500">Platform transaction fee ({ratePct}%)</span>
            <span className="font-bold text-slate-700 dark:text-slate-300">+£{(ex.booking_fee_pence / 100).toFixed(2)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs mt-1 pt-1 border-t border-slate-200 dark:border-slate-700">
          <span className="font-bold text-slate-700 dark:text-slate-200">Pupil pays</span>
          <span data-testid="fee-mode-example-total" className="font-extrabold text-slate-900 dark:text-white">£{(ex.pupil_total_pence / 100).toFixed(2)}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="font-bold text-emerald-700 dark:text-emerald-400">You receive</span>
          <span data-testid="fee-mode-example-net" className="font-extrabold text-emerald-600">£{(ex.instructor_net_pence / 100).toFixed(2)}</span>
        </div>
      </div>
    </section>
  );
}

function ModeOption({ testid, active, saving, onClick, title, subtitle }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={saving}
      className={`w-full text-left rounded-2xl px-3 py-2.5 border-2 transition-all flex items-center gap-3 ${
        active
          ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/15"
          : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-emerald-300"
      } ${saving ? "opacity-60 cursor-wait" : "active:scale-[0.99]"}`}
    >
      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
        active ? "border-emerald-500 bg-emerald-500" : "border-slate-300 dark:border-slate-600"
      }`}>
        {active && (saving ? <Loader2 className="w-3 h-3 text-white animate-spin" /> : <Check className="w-3 h-3 text-white" />)}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-bold ${active ? "text-emerald-700 dark:text-emerald-300" : "text-slate-800 dark:text-slate-200"}`}>{title}</p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{subtitle}</p>
      </div>
    </button>
  );
}
